import { decisionChoice, titleCase, type ClientDecisionDto } from '@nsb/core';
import { useState } from 'react';
import { api, errorText, queryClient } from '../../api.ts';
import { DecisionChoice } from '../../components/DecisionChoice.tsx';
import { RemindAsk, RemindLink } from '../../components/Remind.tsx';
import { useRemind, type Remind } from '../../components/useRemind.ts';
import { toldLine } from '../../components/remindWords.ts';
import { RecordApproval } from '../../components/process/ApprovalRecord.tsx';
import { chooseWords } from '../../components/decisionWords.ts';
import { Button, ErrorLine } from '../../components/ui.tsx';

/**
 * One of the client's own steps the work has reached, waiting on them: what it is, and Mark it decided (or done, or
 * Approve for a gate they approve: design study 09, gates A) with a word if they like (spec §5's click-to-approve). On
 * their home's Waiting on you and their Decisions page alike; NSB, on its Decisions page, marks one for them
 * (`forClient` false: the same row in NSB's words). A decision with options is decided by choosing (design study 22):
 * one of them, or any, as it says, the choice sent with it.
 */
export function YoursRow({
  projectId,
  step: y,
  preview,
  forClient = true,
  drawn = false,
  remind,
}: {
  projectId: string;
  step: Pick<ClientDecisionDto, 'id' | 'title' | 'kind' | 'why' | 'options' | 'choose'>;
  preview: boolean;
  forClient?: boolean;
  /** Drawn only (the Introduction): its words as the home shows them, nothing to press. */
  drawn?: boolean;
  /** NSB's row: who of the client's side was told of it, and Remind them (design study 26; `RemindedYoursRow`). */
  remind?: Remind;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const word = y.kind === 'decision' ? 'decided' : 'done';
  // NSB never approves a client's gate for them with a click: it records the approval they gave outside the portal, with
  // its day and its evidence (design study 24, L-16 A), the form the gate's panel has.
  const recording = !forClient && y.kind === 'gate';
  const act = y.kind === 'gate' ? (recording ? 'Record their approval' : 'Approve') : `Mark it ${word}`;
  // A decision with options: what it offers, and whether what is picked decides it (core `decisionChoice`, the API's rule).
  const options = y.kind === 'decision' ? (y.options ?? []) : [];
  const choose = y.choose ?? 'one';
  const ready = !options.length || !('refused' in decisionChoice({ title: y.title, options, choose }, picked));
  const settle = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`/projects/${projectId}/process/steps/${y.id}/decide`, { body: { note: note.trim() || undefined, ...(options.length ? { choice: picked } : {}) } });
      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      setOpen(false);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="py-3.5">
      <p className="flex items-baseline justify-between gap-4">
        <span>
          <span className="block">{titleCase(y.title)}</span>
          <span className="meta">
            {forClient
              ? y.kind === 'decision'
                ? 'A decision for you'
                : y.kind === 'gate'
                  ? 'Yours to approve'
                  : 'Yours to do'
              : y.kind === 'decision'
                ? 'A decision for the client'
                : y.kind === 'gate'
                  ? 'The client’s to approve'
                  : 'The client’s to do'}
            {options.length ? ` · ${chooseWords(choose).toLowerCase()} of ${options.length}` : ''}
            {remind?.people.length ? ` · ${toldLine(remind.people, remind.today, { row: true })}` : ''}
          </span>
        </span>
        {drawn ? (
          <span className="shrink-0 text-sm text-ink-2 underline decoration-rule-strong underline-offset-[0.3em]">{act}</span>
        ) : !open && !preview && !remind?.open ? (
          <span className="flex shrink-0 flex-col items-end sm:flex-row sm:gap-x-5">
            {remind ? <RemindLink r={remind} /> : null}
            <button type="button" className="link min-h-11 shrink-0 text-sm text-ink-2 hover:text-ink" onClick={() => setOpen(true)}>
              {act}
            </button>
          </span>
        ) : null}
      </p>
      {remind ? <RemindAsk r={remind} /> : null}
      {y.why ? <p className="mt-1 max-w-measure text-[0.9375rem] text-ink-2">{y.why}</p> : null}
      {open && recording ? (
        <RecordApproval
          projectId={projectId}
          stepId={y.id}
          onDone={() => {
            setOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
          }}
          onCancel={() => setOpen(false)}
        />
      ) : open ? (
        <div className="mt-2 rounded-[2px] bg-sheet p-3">
          {options.length ? (
            <DecisionChoice
              legend={chooseWords(choose)}
              options={options}
              choose={choose}
              value={picked}
              onChange={setPicked}
              disabled={busy}
            />
          ) : null}
          <label className="block">
            <span className="meta">
              {y.kind === 'decision' && !options.length
                ? `What ${forClient ? 'you' : 'they'} decided, if you like`
                : y.kind === 'gate'
                  ? `A word on ${forClient ? 'your' : 'their'} approval, if you like`
                  : 'A word on it, if you like'}
            </span>
            <textarea className="field mt-1 min-h-16 w-full" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          <div className="mt-2 flex flex-wrap gap-4">
            <Button tone="secondary" busy={busy} disabled={!ready} onClick={() => void settle()}>
              {act}
            </Button>
            <Button tone="quiet" onClick={() => setOpen(false)}>
              Not yet
            </Button>
          </div>
          <ErrorLine error={err} />
        </div>
      ) : null}
    </li>
  );
}

/** NSB's row on its Decisions page: the same row, with who of the client's side was told of it and Remind them. */
export function RemindedYoursRow(props: Omit<Parameters<typeof YoursRow>[0], 'remind' | 'forClient'>) {
  const remind = useRemind(props.projectId, 'step', props.step.id, { staff: !props.preview });
  return <YoursRow {...props} forClient={false} remind={remind} />;
}
